(() => {
  "use strict";

  const TYPE_FACTOR = {
    onion: 1,
    garlic: 5,
    mild: 1,
    shallot: 1.5,
  };

  const FORM_FACTOR = {
    raw: 1,
    cooked: 1,
    powder: 8,
  };

  const DOG_THRESHOLDS = [5, 15, 30];

  const CHIP_SETS = {
    onion: [
      ["A few rings", "20", "g"],
      ["Half an onion", "55", "g"],
      ["1 whole onion", "1", "onion"],
      ["Bowl of scraps", "200", "g"],
    ],
    garlic: [
      ["1 clove", "1", "clove"],
      ["3 cloves", "3", "clove"],
      ["1 tsp powder", "1", "tsp"],
      ["Garlic bread slice", "8", "g"],
    ],
    mild: [
      ["Small handful", "15", "g"],
      ["Side of chives", "30", "g"],
      ["Leek piece", "60", "g"],
    ],
    shallot: [
      ["1 shallot", "30", "g"],
      ["2 shallots", "60", "g"],
      ["Handful", "80", "g"],
    ],
  };

  const RESULT_CONTENT = {
    low: {
      className: "onion-garlic-toxicity-calculator__band--low",
      tag: "✅ Low risk",
      title: "Likely below the toxic threshold",
      message:
        "Based on what you entered, the dose sits under the level usually linked to red-cell damage. Low risk is not no risk — keep a close eye on your pet.",
      steps: [
        "🚫 Take away any remaining onion or garlic so they cannot eat more.",
        "👀 Watch closely for 3–5 days for vomiting, tiredness, pale gums or dark urine.",
        "📝 Note the time, food and amount in case things change.",
        "📞 If your pet is a cat, very small, unwell or you are unsure, call your vet to be safe.",
      ],
    },
    moderate: {
      className: "onion-garlic-toxicity-calculator__band--moderate",
      tag: "⚠️ Moderate risk",
      title: "At or above the toxic threshold — call your vet",
      message:
        "This dose reaches the level where poisoning can occur. Do not wait for symptoms — because anaemia is delayed, early advice matters most.",
      steps: [
        "📞 Call your vet or a pet poison line now for tailored advice.",
        "📋 Have ready: species, weight, exactly what and how much, and the time.",
        "🚫 Do not make your pet vomit unless a vet tells you to.",
        "👀 Watch for pale or yellow gums, weakness, fast breathing or red-brown urine over the next 1–5 days.",
      ],
    },
    high: {
      className: "onion-garlic-toxicity-calculator__band--high",
      tag: "🟠 High risk",
      title: "Enough to cause anaemia — get veterinary care",
      message:
        "This is within the range reported to cause clinically important red-cell damage. Your pet needs to be seen or spoken to by a vet promptly.",
      steps: [
        {
          text: "🚑 Contact your vet or emergency clinic now and head in if advised.",
          urgent: true,
        },
        "💊 If ingestion was very recent, the vet may induce vomiting and consider decontamination.",
        "🩸 Expect blood tests and monitoring over several days — anaemia can peak 3–5 days later.",
        "📦 Keep the packaging or note the amount to show the clinic.",
      ],
    },
    severe: {
      className: "onion-garlic-toxicity-calculator__band--severe",
      tag: "🚨 Severe risk",
      title: "Emergency — go to a vet immediately",
      message:
        "This dose is well above the level linked to serious haemolytic anaemia. Treat this as a medical emergency.",
      steps: [
        {
          text: "🚨 Go to your nearest vet or emergency animal hospital right now.",
          urgent: true,
        },
        {
          text: "📞 Call ahead so they are ready for you.",
          urgent: true,
        },
        "📋 Bring details: weight, what was eaten, how much and when.",
        "🚫 Do not attempt home treatment or wait to see if symptoms appear.",
      ],
    },
  };

  class OnionGarlicToxicityCalculator extends HTMLElement {
    constructor() {
      super();

      this.state = {
        species: "dog",
      };

      this.config = {
        validation: {},
      };

      this.handleSpeciesClick = this.handleSpeciesClick.bind(this);
      this.handleTypeChange = this.handleTypeChange.bind(this);
      this.handleCalculate = this.handleCalculate.bind(this);
      this.handleKeydown = this.handleKeydown.bind(this);
    }

    connectedCallback() {
      if (this.dataset.initialized === "true") return;

      this.cacheElements();
      this.readConfig();
      this.bindEvents();
      this.buildChips();
      this.syncSpecies();

      this.dataset.initialized = "true";
    }

    disconnectedCallback() {
      this.unbindEvents();
      delete this.dataset.initialized;
    }

    cacheElements() {
      this.elements = {
        speciesGroup: this.querySelector("[data-ogtc-species]"),
        speciesButtons: Array.from(
          this.querySelectorAll("[data-ogtc-species] button[data-value]")
        ),
        weight: this.querySelector("[data-ogtc-weight]"),
        weightUnit: this.querySelector("[data-ogtc-weight-unit]"),
        japaneseField: this.querySelector("[data-ogtc-japanese-field]"),
        japanese: this.querySelector("[data-ogtc-japanese]"),
        type: this.querySelector("[data-ogtc-type]"),
        form: this.querySelector("[data-ogtc-form]"),
        amount: this.querySelector("[data-ogtc-amount]"),
        amountUnit: this.querySelector("[data-ogtc-amount-unit]"),
        chips: this.querySelector("[data-ogtc-chips]"),
        unknown: this.querySelector("[data-ogtc-unknown]"),
        calculate: this.querySelector("[data-ogtc-calculate]"),
        error: this.querySelector("[data-ogtc-error]"),
        results: this.querySelector("[data-ogtc-results]"),
        band: this.querySelector("[data-ogtc-band]"),
        steps: this.querySelector("[data-ogtc-steps]"),
      };
    }

    readConfig() {
      const node = this.querySelector("[data-ogtc-config]");
      if (!node) return;

      try {
        this.config = JSON.parse(node.textContent || "{}");
      } catch (error) {
        console.error(
          "Onion & Garlic Toxicity Calculator: invalid configuration JSON.",
          error
        );
      }
    }

    bindEvents() {
      this.elements.speciesGroup?.addEventListener(
        "click",
        this.handleSpeciesClick
      );
      this.elements.type?.addEventListener("change", this.handleTypeChange);
      this.elements.calculate?.addEventListener("click", this.handleCalculate);

      [this.elements.weight, this.elements.amount]
        .filter(Boolean)
        .forEach((input) => input.addEventListener("keydown", this.handleKeydown));
    }

    unbindEvents() {
      this.elements?.speciesGroup?.removeEventListener(
        "click",
        this.handleSpeciesClick
      );
      this.elements?.type?.removeEventListener("change", this.handleTypeChange);
      this.elements?.calculate?.removeEventListener(
        "click",
        this.handleCalculate
      );

      [this.elements?.weight, this.elements?.amount]
        .filter(Boolean)
        .forEach((input) =>
          input.removeEventListener("keydown", this.handleKeydown)
        );
    }

    handleSpeciesClick(event) {
      const button = event.target.closest("button[data-value]");
      if (!button || !this.elements.speciesGroup?.contains(button)) return;

      this.state.species = button.dataset.value === "cat" ? "cat" : "dog";

      this.elements.speciesButtons.forEach((item) => {
        const active = item === button;
        item.classList.toggle("is-active", active);
        item.setAttribute("aria-pressed", String(active));
      });

      if (this.state.species === "cat" && this.elements.japanese) {
        this.elements.japanese.checked = false;
      }

      this.syncSpecies();
    }

    handleTypeChange() {
      this.buildChips();
    }

    handleCalculate() {
      this.calculate();
    }

    handleKeydown(event) {
      if (event.key !== "Enter") return;

      event.preventDefault();
      this.calculate();
    }

    syncSpecies() {
      if (this.elements.japaneseField) {
        this.elements.japaneseField.hidden = this.state.species !== "dog";
      }
    }

    buildChips() {
      if (!this.elements.chips || !this.elements.type) return;

      this.elements.chips.innerHTML = "";

      const chips = CHIP_SETS[this.elements.type.value] || [];

      chips.forEach(([label, value, unit]) => {
        const button = document.createElement("button");
        button.type = "button";
        button.textContent = label;

        button.addEventListener("click", () => {
          this.elements.amount.value = value;
          this.elements.amountUnit.value = unit;
          this.elements.unknown.checked = false;
        });

        this.elements.chips.appendChild(button);
      });
    }

    calculate() {
      this.hideError();

      const weightValue = Number.parseFloat(this.elements.weight?.value || "");

      if (!Number.isFinite(weightValue) || weightValue <= 0) {
        this.showError(
          this.config.validation?.weight ||
            "Please enter your pet's body weight."
        );
        return;
      }

      const weightKg =
        this.elements.weightUnit?.value === "lb"
          ? weightValue * 0.45359237
          : weightValue;

      if (this.elements.unknown?.checked) {
        this.renderUnknown();
        return;
      }

      const amountValue = Number.parseFloat(this.elements.amount?.value || "");

      if (!Number.isFinite(amountValue) || amountValue <= 0) {
        this.showError(
          this.config.validation?.amount ||
            "Enter how much they ate, or tick amount unknown."
        );
        return;
      }

      const type = this.elements.type?.value || "onion";
      const form = this.elements.form?.value || "raw";
      const grams = this.unitToGrams(
        amountValue,
        this.elements.amountUnit?.value || "g",
        type
      );
      const onionEquivalentGrams =
        grams * (TYPE_FACTOR[type] || 1) * (FORM_FACTOR[form] || 1);
      const realDose = onionEquivalentGrams / weightKg;

      let sensitivity = 1;

      if (this.state.species === "cat") sensitivity = 3;
      if (
        this.state.species === "dog" &&
        this.elements.japanese?.checked
      ) {
        sensitivity = 2;
      }

      const effectiveDose = realDose * sensitivity;
      this.renderResult(effectiveDose, realDose, onionEquivalentGrams);
    }

    unitToGrams(value, unit, type) {
      switch (unit) {
        case "g":
          return value;
        case "oz":
          return value * 28.349523125;
        case "clove":
          return value * 4;
        case "onion":
          return value * 110;
        case "tsp":
          return value * (type === "garlic" ? 3 : 2.5);
        case "tbsp":
          return value * (type === "garlic" ? 9 : 7.5);
        default:
          return value;
      }
    }

    riskBand(effectiveDose) {
      if (effectiveDose < DOG_THRESHOLDS[0]) return "low";
      if (effectiveDose < DOG_THRESHOLDS[1]) return "moderate";
      if (effectiveDose < DOG_THRESHOLDS[2]) return "high";
      return "severe";
    }

    renderResult(effectiveDose, realDose, onionEquivalentGrams) {
      const result = RESULT_CONTENT[this.riskBand(effectiveDose)];
      const sensitive =
        this.state.species === "cat" || this.elements.japanese?.checked;

      this.elements.band.className =
        `onion-garlic-toxicity-calculator__band ${result.className}`;

      this.elements.band.innerHTML = `
        <span class="onion-garlic-toxicity-calculator__band-tag">
          ${result.tag}
        </span>
        <h3>${result.title}</h3>
        <p>${result.message}</p>
        <div class="onion-garlic-toxicity-calculator__dose">
          Estimated dose:
          <strong>${realDose.toFixed(1)} g/kg</strong> onion-equivalent
          ${sensitive ? " · scored conservatively for a sensitive patient" : ""}
          · ~${Math.round(onionEquivalentGrams)} g onion-equivalent total
        </div>
      `;

      this.renderSteps(result.steps);
      this.showResults();
    }

    renderUnknown() {
      this.elements.band.className =
        "onion-garlic-toxicity-calculator__band onion-garlic-toxicity-calculator__band--high";

      this.elements.band.innerHTML = `
        <span class="onion-garlic-toxicity-calculator__band-tag">
          ❓ Unknown amount
        </span>
        <h3>Cannot measure it? Treat it as a risk</h3>
        <p>
          When you do not know how much was eaten, assume the worst and get
          advice — especially for cats, small pets and Japanese breeds. It is
          safer to check than to miss a delayed poisoning.
        </p>
      `;

      this.renderSteps([
        {
          text: "📞 Call your vet or a pet poison line now with your pet's species, weight and what they got into.",
          urgent: true,
        },
        "🔎 Remove any remaining onion or garlic and check what is missing to estimate the amount.",
        "👀 Watch closely for 1–5 days: pale gums, weakness, fast breathing or dark urine.",
        "📦 Save any packaging to show the vet.",
      ]);

      this.showResults();
    }

    renderSteps(steps) {
      this.elements.steps.innerHTML = "";

      steps.forEach((step) => {
        const item = document.createElement("li");

        if (typeof step === "object") {
          item.textContent = step.text;
          item.classList.toggle("is-urgent", Boolean(step.urgent));
        } else {
          item.textContent = step;
        }

        this.elements.steps.appendChild(item);
      });
    }

    showResults() {
      this.elements.results.hidden = false;

      window.requestAnimationFrame(() => {
        this.elements.results.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    }

    showError(message) {
      if (!this.elements.error) return;

      this.elements.error.textContent = message;
      this.elements.error.hidden = false;
      this.elements.error.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }

    hideError() {
      if (!this.elements.error) return;

      this.elements.error.hidden = true;
      this.elements.error.textContent = "";
    }
  }

  if (!customElements.get("s-onion-garlic-toxicity-calculator")) {
    customElements.define(
      "s-onion-garlic-toxicity-calculator",
      OnionGarlicToxicityCalculator
    );
  }
})();
